import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Check } from "lucide-react";
import Marquee from "../components/Marquee";
import Testimonials from "../components/Testimonials";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BRAND, SERVICES, GALLERY, PROMOS, IMAGES } from "../data/siteData";

const heroLines = ["Great lawns", "aren't born —", "they're crafted."];

const STATS = [
  ["500+", "Properties groomed"],
  ["9", "Signature services"],
  ["4", "Seasons covered"],
  ["24/7", "Winter dispatch"],
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const featured = SERVICES.filter((s) => ["grass-cutting", "interlocking", "gardens"].includes(s.id));

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-[#FBF9F5] pt-28 lg:pt-36">
        <div className="mx-auto grid max-w-7xl grid-cols-12 gap-10 px-5 pb-20 lg:px-8 lg:pb-28">
          <div className="col-span-12 flex flex-col justify-center lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Eyebrow>Skloccom · Lawn & Garden Care · Ontario</Eyebrow>
            </motion.div>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display text-5xl font-black leading-[1.05] tracking-tight text-[#1A2E20] sm:text-6xl lg:text-7xl"
            >
              {heroLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-[#4a544c] sm:text-lg"
            >
              Precision mowing, bespoke gardens, stonework and four-season property care — crafted for homeowners
              who notice the details.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/booking"
                data-testid="hero-get-quote-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-[#1A2E20] px-7 py-3.5 text-sm font-semibold text-[#FBF9F5] transition-colors duration-200 hover:bg-[#2E6941]"
              >
                Book a Free Quote
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${BRAND.phone}`}
                data-testid="hero-call-now-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A2E20]/25 px-7 py-3.5 text-sm font-semibold text-[#1A2E20] transition-colors duration-200 hover:border-[#2E6941] hover:text-[#2E6941]"
              >
                <Phone size={16} />
                {BRAND.phoneDisplay}
              </a>
            </motion.div>
          </div>

          <div className="relative col-span-12 min-h-[420px] lg:col-span-5 lg:min-h-[560px]">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 overflow-hidden rounded-t-[10rem] border border-[#234E33]/15"
            >
              <img src={IMAGES.hero} alt="Manicured estate lawn crafted by Skloccom" className="h-full w-full object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05 }}
              className="absolute -left-2 bottom-8 border border-[#FBF9F5]/10 bg-[#1A2E20] px-6 py-5 shadow-2xl lg:-left-8"
            >
              <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.3em] text-[#72B043]">Est. 2022</p>
              <p className="mt-1 font-display text-2xl font-bold text-[#FBF9F5]">500+ properties groomed</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* ABOUT PREVIEW */}
      <section className="bg-[#FBF9F5] py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden border border-[#234E33]/15">
              <img src={IMAGES.about} alt="Fresh sod being laid by the Skloccom crew" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>About Skloccom</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A2E20] sm:text-4xl">
              We dig our job — literally.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-[#4a544c]">
              Skloccom was built on a simple belief: a property's first impression starts at the curb. Our crew treats
              every lawn, bed and stone path like a piece of craft — measured, edged and finished with care, at a
              price that stays honest.
            </p>
            <ul className="mt-6 space-y-3">
              {["Owner-operated, detail-obsessed crews", "Organic-first products, safe for kids & pets", "One call covers all four seasons"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-[#1A2E20]">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#2E6941]" />
                  {t}
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#2E6941] transition-colors hover:text-[#1A2E20]"
            >
              Meet the crew <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="bg-[#F4EFEA] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>What We Do</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A2E20] sm:text-4xl">
                Signature services
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="home-all-services-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E6941] transition-colors hover:text-[#1A2E20]"
            >
              All nine services <ArrowRight size={15} />
            </Link>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8" data-testid="services-bento-grid">
            {featured.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group block border border-[#234E33]/12 bg-[#FAF7F2] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-7">
                    <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C86D51]">{s.type}</p>
                    <h3 className="mt-2 font-display text-xl font-bold text-[#1A2E20]">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#4a544c]">{s.description}</p>
                    <p className="mt-4 text-sm font-semibold text-[#2E6941]">{s.pricing}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="bg-[#1A2E20] py-16">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-5 lg:grid-cols-4 lg:px-8">
          {STATS.map(([num, label], i) => (
            <Reveal key={label} delay={i * 0.08} className="text-center lg:text-left">
              <p className="font-display text-4xl font-black text-[#FBF9F5] sm:text-5xl">{num}</p>
              <p className="mt-2 font-mono-x text-xs font-semibold uppercase tracking-[0.25em] text-[#72B043]">{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROMOTIONS */}
      <section className="bg-[#FBF9F5] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Eyebrow>Current Promotions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A2E20] sm:text-4xl">
              Good craft loves a good deal
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {PROMOS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <div
                  data-testid={`promo-card-${p.id}`}
                  className="flex h-full flex-col border border-[#234E33]/12 bg-[#FAF7F2] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="inline-block w-fit bg-[#2E6941]/10 px-3 py-1 font-mono-x text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1A2E20]">
                    {p.badge}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold text-[#1A2E20]">{p.headline}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4a544c]">{p.detail}</p>
                  <p className="mt-5 font-mono-x text-xs font-semibold tracking-[0.15em] text-[#C86D51]">CODE · {p.code}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY TEASER */}
      <section className="bg-[#F4EFEA] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Selected Works</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A2E20] sm:text-4xl">
                Proof, not promises
              </h2>
            </div>
            <Link
              to="/gallery"
              data-testid="home-gallery-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E6941] transition-colors hover:text-[#1A2E20]"
            >
              View the gallery <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {GALLERY.slice(0, 3).map((g, i) => (
              <Reveal key={g.title} delay={i * 0.1}>
                <Link to="/gallery" className="group block overflow-hidden border border-[#234E33]/12">
                  <div className="overflow-hidden">
                    <img
                      src={g.url}
                      alt={g.title}
                      className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="bg-[#FAF7F2] p-5">
                    <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C86D51]">{g.category}</p>
                    <p className="mt-1 font-display text-lg font-bold text-[#1A2E20]">{g.title}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-[#1A2E20] py-24 lg:py-32">
        <img src={IMAGES.cta} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-4xl font-black leading-tight tracking-tight text-[#FBF9F5] sm:text-5xl">
              Ready when the grass is.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#FBF9F5]/70">
              One call books your free on-site quote. Most estimates are back in your inbox within one business day.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a
                href={`tel:${BRAND.phone}`}
                data-testid="cta-call-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#72B043] px-8 py-4 text-sm font-bold text-[#0C0E0D] transition-colors duration-200 hover:bg-[#FBF9F5]"
              >
                <Phone size={16} />
                Call {BRAND.phoneDisplay}
              </a>
              <Link
                to="/booking"
                data-testid="cta-quote-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#FBF9F5]/30 px-8 py-4 text-sm font-semibold text-[#FBF9F5] transition-colors duration-200 hover:border-[#72B043] hover:text-[#72B043]"
              >
                Book a Free Visit
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
